import React, { useState } from 'react';
import { Eye, Search, X } from 'lucide-react';
import Card from '../../Components/Ui/Card';
import Table from '../../Components/Ui/Table';
import Button from '../../Components/Ui/Button';
import Badge from '../../Components/Ui/Badge';
import { statusVariants, paymentVariants } from '../../Data/Dummydata';
import { useNavigate } from 'react-router-dom';
import { useGetSellerOrdersQuery } from '../../app/services/orderApi';

const Orders = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const navigate = useNavigate();

  const {
    data: ordersData,
    isLoading,
    isError,
    error,
  } = useGetSellerOrdersQuery({
    page,
    limit,
    status: statusFilter,
    search: searchQuery,
    paymentStatus: paymentStatusFilter,
    date: dateFilter,
  });

  const orders = ordersData?.data || [];
  const pagination = ordersData?.pagination || {};

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const getStatusBadge = (status) => (
    <Badge variant={statusVariants[status] || 'primary'} className="text-nowrap">
      {capitalize(status || 'pending')}
    </Badge>
  );

  const getPaymentStatusBadge = (status) => (
    <Badge variant={paymentVariants[status] || 'primary'} className="text-nowrap">
      {capitalize(status || 'pending')}
    </Badge>
  );

  const tableColumns = [
    { header: 'Order ID', accessor: 'orderId' },
    {
      header: 'Retailer',
      accessor: ({ user }) => (
        <div>
          <p className="font-medium">{user?.name || 'Unknown'}</p>
          <p className="text-sm text-gray-500">{user?.email || 'N/A'}</p>
        </div>
      ),
    },
    {
      header: 'Products',
      accessor: ({ products }) => (
        <div>
          <p className="font-medium">{products?.[0]?.productsId?.title || 'Unknown Product'}</p>
          {products?.length > 1 && (
            <p className="text-xs text-gray-500">+{products.length - 1} more items</p>
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      className: 'text-center',
      accessor: (row) => (
        <div className="flex flex-col items-center space-y-1 text-nowrap">
          {getStatusBadge(row.status)}
          {getPaymentStatusBadge(row.paymentStatus)}
        </div>
      ),
    },
    {
      header: 'Total',
      className: '',
      accessor: ({ totalAmount }) => (
        <span className="font-medium">₹{totalAmount?.toFixed(2) || '0.00'}</span>
      ),
    },
    {
      header: 'Date',
      accessor: ({ createdAt }) =>
        new Date(createdAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
      className: 'whitespace-nowrap',
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => navigate('/seller/orders/details', { state: { order: row } })}
            className="flex items-center gap-1"
          >
            <Eye size={16} />
            View
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) return <div className="text-center py-10">Loading orders...</div>;
  if (isError)
    return (
      <div className="text-center py-10 text-red-500 font-medium">
        Error: {error.data?.message || 'Failed to fetch orders'}
      </div>
    );

  return (
    <div className="space-y-6 px-2 md:px-0 lg:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 justify-center items-center">
        <h2 className="text-lg lg:text-2xl font-bold pl-6">Orders</h2>
      </div>

      <Card>
        <div className="flex flex-col items-center justify-center lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Retailer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-secondary hover:bg-gray-50 focus:outline-none focus:ring-2 border-color w-full"
            />
            {searchQuery && (
              <X
                className="absolute right-3 top-2.5 h-4 w-4 text-gray-500 cursor-pointer hover:text-secondary"
                onClick={() => setSearchQuery('')}
              />
            )}
          </div>

          <div className="flex flex-wrap gap-2 items-center justify-center">
            <select
              className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-primary focus:outline-none focus:ring-2"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Status</option>
              {Object.keys(statusVariants).map((status) => (
                <option key={status} value={status}>
                  {capitalize(status)}
                </option>
              ))}
            </select>
            <select
              className="border border-gray-300 rounded-lg text-sm p-2 focus:ring-primary focus:outline-none focus:ring-2"
              value={paymentStatusFilter}
              onChange={(e) => {
                setPaymentStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Payment Status</option>
              {Object.keys(paymentVariants).map((status) => (
                <option key={status} value={status}>
                  {capitalize(status)}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-primary focus:outline-none focus:ring-2"
            />
          </div>
        </div>

        <Table columns={tableColumns} data={orders} keyExtractor={(row) => row._id} />

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            Showing {orders.length} of {pagination.totalItems || 0} orders
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm font-medium">
              Page {page} of {pagination.totalPages || 1}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
              disabled={page === (pagination.totalPages || 1)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Orders;
